import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogToolUsage } from "@/hooks/useLogToolUsage";
import { parseTimestamp, parseUtcDateTime, relativeTime, toUtcInputValue } from "./timestampUtils";
import { copyText } from "@/lib/copyText";

const TOOL_INFO_CONTENT = {
  what: `Converts between Unix timestamps (the number of seconds since January 1, 1970) and human-readable dates, in either direction.`,
  how: `Enter a timestamp or a date, and the other format updates automatically. A "use current time" button fills in the current timestamp instantly.`,
  tip: `Watch whether your timestamp is in seconds or milliseconds — a 13-digit number is typically milliseconds and a 10-digit number is typically seconds (this tool reads anything from 100,000,000,000, which is 12 digits, upwards as milliseconds), and mixing them up gives a wildly wrong date.`,
};

export default function TimestampConverter() {
  const [timestamp, setTimestamp] = useState(
    String(Math.floor(Date.now() / 1000))
  );
  const [dateStr, setDateStr] = useState(toUtcInputValue(Date.now()));

  const parsed = parseTimestamp(timestamp);

  const fromTimestamp = (value: string) => {
    setTimestamp(value);
    const p = parseTimestamp(value);
    if (p.ok) setDateStr(toUtcInputValue(p.ms));
  };

  const fromDate = (value: string) => {
    setDateStr(value);
    const ms = parseUtcDateTime(value);
    if (ms !== null) setTimestamp(String(Math.floor(ms / 1000)));
  };

  const useNow = () => {
    const now = Date.now();
    setTimestamp(String(Math.floor(now / 1000)));
    setDateStr(toUtcInputValue(now));
  };

  const copy = (v: string) => {
    void copyText(v, "Copied to clipboard");
  };

  const humanReadable = parsed.ok ? new Date(parsed.ms).toString() : "";
  const isoOut = parsed.ok ? new Date(parsed.ms).toISOString() : "";
  const relative = parsed.ok ? relativeTime(parsed.ms) : "";

  const { blocked } = useLogToolUsage(humanReadable, undefined, { ownActions: true });


  return (
    <ToolContainer>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="ts-input" className="mb-2 block">Unix Timestamp (seconds or ms)</Label>
          <Input
            id="ts-input"
            value={timestamp}
            onChange={e => fromTimestamp(e.target.value)}
            className="font-mono"
            aria-invalid={!parsed.ok && !!parsed.error}
          />
          {!parsed.ok && parsed.error && (
            <p className="text-sm text-destructive mt-1">{parsed.error}</p>
          )}
          {parsed.ok && (
            <p className="text-xs text-muted-foreground mt-1">
              Read as {parsed.unit} (12 digits or more, from 100,000,000,000, is read as milliseconds).
            </p>
          )}
        </div>
        <div>
          <Label htmlFor="ts-date" className="mb-2 block">Date / Time (UTC)</Label>
          <Input
            id="ts-date"
            type="datetime-local"
            step="1"
            value={dateStr}
            onChange={e => fromDate(e.target.value)}
          />
        </div>
      </div>

      <Button variant="outline" className="mt-4" onClick={useNow}>
        Use Current Time
      </Button>

      {humanReadable && !blocked && (
        <Card className="mt-4 p-4 space-y-3">
          {[
            ["Local time", humanReadable],
            ["ISO 8601 (UTC)", isoOut],
            ["Relative", relative],
          ].map(([label, value]) => (
            <div key={label}>
              <p className="text-sm text-muted-foreground mb-1">{label}</p>
              <div className="flex items-center justify-between gap-3">
                <p className="font-medium break-all">{value}</p>
                <button
                  className="text-xs text-primary hover:underline shrink-0"
                  onClick={() => copy(value)}
                >
                  Copy
                </button>
              </div>
            </div>
          ))}
        </Card>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
